import React, { useEffect, useState } from 'react';
import { ArrowLeft, Users, PlayCircle, Brain, CheckCircle2, Target, Award, ShieldCheck } from 'lucide-react';
import { PILLAR_THEME } from '../components/learning/VisualMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OrgLearningDashboard = ({ onBack = () => {} }) => {
  const orgId = sessionStorage.getItem('cness_organization_id') || '';
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!orgId) return;
    fetch(`${BACKEND_URL}/api/learning-hub/org-dashboard/${orgId}`).then((r) => r.json()).then(setData);
  }, [orgId]);

  if (!orgId) {
    return <p className="text-base text-slate-500" data-testid="org-dashboard-no-org">No organisation is linked to this session.</p>;
  }
  if (!data) {
    return <div className="min-h-[40vh] flex items-center justify-center text-slate-400">Loading organisation learning…</div>;
  }

  const t = data.totals;
  const tiles = [
    { key: 'learners', label: 'Learners', value: t.learners, icon: Users },
    { key: 'started', label: 'Courses started', value: t.courses_started, icon: PlayCircle },
    { key: 'meaningful', label: 'Meaningful engagement', value: t.meaningful_engagement, icon: Brain },
    { key: 'completed', label: 'Courses completed', value: t.completed, icon: CheckCircle2 },
    { key: 'actions', label: 'Actions committed', value: t.actions_committed, icon: Target },
    { key: 'certificates', label: 'Certificates issued', value: t.certificates_issued ?? 0, icon: Award },
  ];

  return (
    <div className="space-y-10" data-testid="org-learning-dashboard">
      <div>
        <button onClick={onBack} data-testid="org-back-btn" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 mb-5">
          <ArrowLeft className="h-4 w-4" /> Learning Hub
        </button>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Organisation learning</h1>
        <p className="text-base text-slate-500 mt-2 max-w-2xl">
          Participation across the six-pillar programme. Individual reflections stay private to each learner.
        </p>
      </div>

      <section className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        {tiles.map((tile) => (
          <div key={tile.key} data-testid={`org-tile-${tile.key}`} className="bg-white rounded-2xl border border-slate-200 p-6">
            <tile.icon className="h-5 w-5 text-emerald-700 mb-3" />
            <p className="text-3xl font-bold text-slate-900">{tile.value}</p>
            <p className="text-sm text-slate-500 mt-1">{tile.label}</p>
          </div>
        ))}
      </section>

      <section>
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-6">Coverage by pillar</h2>
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {data.pillar_coverage.map((p) => (
            <div key={p.pillar_number} data-testid={`org-pillar-row-${p.pillar_number}`} className="flex flex-wrap items-center gap-4 px-6 py-5 border-b border-slate-100 last:border-0">
              <span className="h-9 w-9 shrink-0 rounded-full text-white text-sm font-bold flex items-center justify-center" style={{ backgroundColor: PILLAR_THEME[p.pillar_number].primary }}>
                {p.pillar_number}
              </span>
              <span className="text-base font-semibold text-slate-900 flex-1 min-w-[12rem]">{p.title}</span>
              <span className="text-sm text-slate-500">{p.learners} learner{p.learners === 1 ? '' : 's'}</span>
              <span className="text-sm text-slate-500">{p.meaningful} meaningful</span>
              <span className="text-sm font-semibold text-emerald-700">{p.completed} completed</span>
            </div>
          ))}
        </div>
      </section>

      <div className="flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-4" data-testid="org-privacy-boundary">
        <ShieldCheck className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-emerald-900">{data.privacy_boundary}</p>
          <p className="text-sm text-emerald-900 mt-1">{data.firewall_notice}</p>
        </div>
      </div>
    </div>
  );
};

export default OrgLearningDashboard;
